import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { camelCase, isEqual } from "lodash";
import i18next from "i18next";
import { setQueryParams } from "./redux/slices/queryParamsSlice";
import Inbound from "./pages/workbench_build/tabs/Inbound";
import Outbound from "./pages/workbench_build/tabs/Outbound";
import { ThemeProvider } from "@mui/material/styles";
import { muiTheme } from "./muiTheme";
import { Box, Button, Snackbar, Alert } from "@mui/material";
import TabPanel from "./components/TabPanel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InputIcon from "@mui/icons-material/Input";
import OutputIcon from "@mui/icons-material/Output";
import CategoryIcon from "@mui/icons-material/Category";
import { useTranslation } from "react-i18next";
import SaveButton from "./components/Save/SaveButton";
import { treeActions } from "./redux/slices/treeSlice";
import HelpPanel from "./components/Help/HelpPanel";
import { api } from './redux/api';
import RepositoryButton from "./components/Repository/RepositoryButton";
import WorkbenchBuild from "./pages/workbench_build/WorkbenchBuild";
import ApiHelp from "./pages/api_help/ApiHelp";
import LoadingScreen from "./components/LoadingScreen";
import PortalHeaderClone from "./components/PortalHeaderClone";
import PortalFooterClone from "./components/PortalFooterClone";
import { ErrorBoundary } from "react-error-boundary";
import axios from "axios";
import { PageErrorFallback } from "./components/PageErrorFallback";
import { setUserPermissions, userPermissionsSlice } from "./redux/slices/userPermissionSlice";
import Deploy from "./pages/deploy/Deploy";
import { customDecoding } from "./utils/utilFunctions";
import RepositoryPage from "./pages/repository/RepositoryPage";
import DeployToRemoteServer from "./pages/deploy/DeployToRemoteServer";
import SchedulerPage from "./pages/scheduler/SchedulerPage";
import APIManagementPage from "./components/Admin/APIManagement";

export default function App() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const state = useSelector(state => state);
  const [page, setPage] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [urlParams, setUrlParams] = useState(undefined);
  const [errorSending, setErrorSending] = useState(false);
  
  // Global toast state for API errors
  const [globalToast, setGlobalToast] = useState({
    open: false,
    message: '',
    severity: 'error'
  });

  //Build Trigger

  // Run this effect once on app load.
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    let queryObj = {
      baseUrl: '',
      account: '',
      user: '',
      workflowGroupName: '',
      template: '',
      tab: '',
      lang: 'en_US', //default en if none given
      token: ""
    }

    for (const [key, value] of queryParams) {
      const camelCaseKey = camelCase(key);
      // Data passed in via URL is not the same as the persisted data. We need to update our end.
      switch (camelCaseKey) {
        case "baseUrl":
          queryObj.baseUrl = value;
          break;
        case "account":
          queryObj.account = value;
          break;
        case "user":
          queryObj.user = value;
          break;
        case "workflowGroupName":
          queryObj.workflowGroupName = customDecoding(value);
          break;
        case "template":
          queryObj.template = customDecoding(value);
          break;
        case "tab":
          queryObj.tab = value;
          break;
        case "lang":
          queryObj.lang = value;
          i18next.changeLanguage(value);
          break;
        case "devPortal":
          queryObj.devPortal = value;
          break;
        case "token":
          queryObj.token = value;
          break;
        case "page":
          queryObj.page = value;
          break;
        default:
          break;
      }
    }

    //fetch user permissions and save to userPermissionSlice
    if (queryObj.baseUrl && queryObj.account && queryObj.user && queryObj.token) {
      axios.get(
        `${queryObj.baseUrl}/workbench?user=${queryObj.user}&account=${queryObj.account}&action=get_user_permission`
      ,{
        headers: {
          "Authorization": `Bearer ${queryObj.token}`
        }
      }).then((response) => {
        if (response?.data?.data?.length > 0) {
          dispatch(setUserPermissions(response.data.data));
          // dispatch(userPermissionsSlice.setUserPermissions(response.data.data));
        } else {
          console.warn("No User Permissions found for user. ", response);
        }
      }).catch((error) => {
        console.warn(error);
      });
    }

    dispatch(setQueryParams(queryObj));
    setUrlParams(queryObj);
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (loaded) {
      try {
        setTimeout(() => {
          if (document.getElementById("initialLoadingSpinnerContainer"))
            document.getElementById("initialLoadingSpinnerContainer").remove();
        }, 250) // TODO: timeout for testing. will be removed in future.
      } catch (error) {
        console.warn(error); 
      }
    }
  }, [loaded])

  const RoutePage = () => {
    if (urlParams?.page) {
      switch (urlParams.page) {
        case "workbench":
          return <WorkbenchBuild urlParams={urlParams} />
        case "api_help":
          return <ApiHelp urlParams={urlParams} />
        case "deploy":
          return <Deploy urlParams={urlParams} />
        case "trading_partner":
          return <RepositoryPage urlParams={urlParams} pageType="trading_partner" />
        case "account_data":
          return <RepositoryPage urlParams={urlParams} pageType="account_data" />
        case "pdc_admin":
          return <RepositoryPage urlParams={urlParams} pageType="pdc_admin" />
        case "tracking_reports":
          return <RepositoryPage urlParams={urlParams} pageType="tracking_reports" />
        case "deploy_to_remote_server":
          return <DeployToRemoteServer urlParams={urlParams} />  
        case "scheduler":
          return <SchedulerPage urlParams={urlParams} />
        case "api_management":
          return <APIManagementPage urlParams={urlParams} />
      }
    } else {
      //temporary default page.
      //TODO: make a landing page incase issue with "page" param
      return (<span>{t("CONTENT_UNDER_CONSTRUCTION")}</span>);
    }

  }

  useEffect(() => {
    // After urlParams is set — setPage inside the "page" case ran before setUrlParams and captured stale urlParams (blank or wrong route).
    if (loaded && urlParams) setPage(<RoutePage />);
  }, [loaded, urlParams]);

  // Global toast listener for API network errors
  useEffect(() => {
    const handleGlobalToast = (event) => {
      const { message, severity } = event.detail;
      setGlobalToast({
        open: true,
        message: message,
        severity: severity || "error"
      });
    };

    window.addEventListener('showToast', handleGlobalToast);
    
    return () => {
      window.removeEventListener('showToast', handleGlobalToast);
    };
  }, []);

  const handleCloseGlobalToast = () => {
    setGlobalToast(prev => ({ ...prev, open: false }));
  };

  useEffect(() => {
    //log errors here, using window.onerror to conform to backend needs.
    window.onerror = function(message, source, lineno, colno, error) {
      //prevent double error send. prevent error send in dev env.
      //Leave this log in for dev purposes.
      console.log(process.env.NODE_ENV);
      if (!errorSending && process.env.NODE_ENV !== "development1") {
        setErrorSending(true);
        let msg = encodeURIComponent(`BridgeGate React Workbench Exception: ` + message);
        let stack = encodeURIComponent(error?.stack);
        const errurl = `${urlParams.baseUrl}/workbench?action=report_js_error&user=${urlParams.user}&source=${source}&message=${msg}&lineno=${lineno}&colno=${colno}&error=${stack}`;

        //send error to backend, and log status of response.
        axios.get(
          errurl,
          {
            headers: {
              "Authorization": `Bearer ${urlParams.token}`
            }
          }
        ).then(res => {
          //Leave this for now. We will need to log any errors in production console until
          //error handling is confirmed success.
          console.log(res);
          setErrorSending(false);
        });


        // this would be the POST alternative if we want to change how the data is sent to backend.
        // axios.post(
        //   errurl,
        //   {
        //     env: process.env.NODE_ENV,
        //     user: urlParams.user,
        //     source: source,
        //     lineno: lineno,
        //     colno: colno,
        //     msg: msg,
        //     error: stack,
        //   },
        //   {
        //     headers: {
        //       "Authorization": `Bearer ${urlParams.token}`
        //     }
        //   }
        // ).then(res => {
        //   //Leave this for now. We will need to log any errors in production console until
        //   //error handling is confirmed success.
        //   console.log(res);
        //   setErrorSending(false);
        // });
      }
    }
    return;
  })

  return (
    <>
      {urlParams && urlParams.devPortal ? (
        <div style={styles.pContain}>
          <PortalHeaderClone user={urlParams.user || 'admin'} account={urlParams.account || 'samples'}/>
          <iframe
            allow="clipboard-read; clipboard-write"
            // width="100%" height="100%" 
            style={styles.iframeStyle}
            src={`http://localhost:3000/?base_url=${urlParams.baseUrl || ''}` +
              (urlParams.account ? (`&account=` + urlParams.account) : '') +
              (urlParams.user ? (`&user=` + urlParams.user) : '') +
              (urlParams.page ? (`&page=` + urlParams.page) : '') +
              (urlParams.tab ? (`&tab=` + urlParams.tab) : '') +
              (urlParams.lang ? (`&lang=` + urlParams.lang) : '') +
              (urlParams.workflowGroupName ? (`&workflowGroupName=` + urlParams.workflowGroupName) : '') +
              (urlParams.template ? (`&template=` + urlParams.template) : '') +
              (urlParams.token ? (`&token=` + urlParams.token) : '')
            }
          >
          </iframe>
          <PortalFooterClone/>
        </div>
      ) : (
        <ErrorBoundary 
          FallbackComponent={PageErrorFallback}
          // onError={logError}
          // onReset={(details) => {
            // Reset the state of the app
            // console.log(details);
          // }}
        >
          <ThemeProvider theme={muiTheme}>
            {page}
          </ThemeProvider>
        </ErrorBoundary>
      )}
      
      {/* Global Toast for API Network Errors */}
      <Snackbar
        open={globalToast.open}
        autoHideDuration={6000}
        onClose={handleCloseGlobalToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseGlobalToast}
          severity={globalToast.severity}
          variant="filled"
          sx={{ width: '100%', maxWidth: '450px', whiteSpace: 'normal', wordBreak: 'break-word' }}
        >
          {globalToast.message}
        </Alert>
      </Snackbar>
    </>
  );
}

const styles = {
  pContain: {
    display: "flex",
    flexDirection: "column",
    height: "100%"
  },
  headerContainer: {
    display: "flex",
    backgroundColor: "#243d7d",
  },
  iframeStyle: {
    width: "calc(100% - 5px)",
    flex: "1",
    border: "none",
    overflow: "hidden"
  },
}


